import { OrganizationSelections } from '@socialgoodsoftware/libraries/services/organization-selection';
import type { OrganizationSelection } from './types';

const statusIntervalProductionMs = 10_000;
export const statusIntervalDevelopmentMs = 60_000;
const validateSessionUrl = '/api/validate-session';
const extendSessionUrl = '/api/extend-session';
export const sessionStatusLockName = 'sgs-session-status';

export type SessionValidity = {
  authenticated: boolean;
  organizationDeactivated?: boolean;
  name?: string | null;
  precedence?: number | null;
  idleTimeoutHours?: number;
  warningMinutes?: number;
  /** How long the server row still has, in ms — sent as a remaining span so a skewed browser clock cannot misread it. */
  expiresInMs?: number;
};

export type SessionStatusOptions = {
  extend?: boolean;
};

export type LockRequestFn = (
  name: string,
  options: { signal?: AbortSignal },
  callback: () => Promise<unknown>
) => Promise<unknown>;

export type LeaderElector = {
  start: () => void;
  stop: () => void;
  isLeader: () => boolean;
};

type LeaderElectorHandlers = {
  onBecomeLeader: () => void;
  onResignLeader: () => void;
};

export type SessionStatusDeps = {
  intervalMs: number;
  lockName: string;
  lockRequest: LockRequestFn | null;
  validate: () => Promise<SessionValidity>;
  onUnauthenticated: () => void;
  onOrgDeactivated?: () => void;
  // switched is false on the first observation when no initialObservedOrg seed was supplied:
  // there was no known org to compare against, so this establishes the baseline rather than
  // reporting a change.
  onOrgChange?: (selection: OrganizationSelection, switched: boolean) => void;
  initialObservedOrg?: string;
  setInterval: (callback: () => void, ms: number) => number;
  clearInterval: (handle: number) => void;
};

export function resolveStatusInterval(isProduction: boolean): number {
  let interval = statusIntervalDevelopmentMs;
  if (isProduction) {
    interval = statusIntervalProductionMs;
  }
  return interval;
}

export async function fetchSessionStatus(
  fetchFn: typeof fetch,
  options?: SessionStatusOptions
): Promise<SessionValidity> {
  // Extending is its own endpoint: the status read cannot slide the window by accident, whatever the caller asks for.
  let url = validateSessionUrl;
  if (options?.extend === true) {
    url = extendSessionUrl;
  }
  const response = await fetchFn(url, { credentials: 'same-origin' });
  let validity: SessionValidity;
  if (response.status === 401) {
    validity = { authenticated: false };
    let code: string | undefined = undefined;
    try {
      const body = (await response.json()) as { code?: string };
      code = body.code;
    } catch {
      code = undefined;
    }
    if (code === 'organizationDeactivated') {
      validity.organizationDeactivated = true;
    }
  } else if (response.ok) {
    const data = (await response.json()) as {
      valid?: boolean;
      name?: string | null;
      precedence?: number | null;
      idleTimeoutHours?: number | null;
      warningMinutes?: number | null;
      expiresInMs?: number | null;
    };
    validity = { authenticated: data.valid === true };
    const selection = OrganizationSelections.From(data);
    if (selection) {
      validity.name = selection.name;
      validity.precedence = selection.precedence;
    }
    if (typeof data.idleTimeoutHours === 'number' && Number.isFinite(data.idleTimeoutHours)) {
      validity.idleTimeoutHours = data.idleTimeoutHours;
    }
    if (typeof data.warningMinutes === 'number' && Number.isFinite(data.warningMinutes)) {
      validity.warningMinutes = data.warningMinutes;
    }
    if (typeof data.expiresInMs === 'number' && Number.isFinite(data.expiresInMs)) {
      validity.expiresInMs = data.expiresInMs;
    }
  } else {
    validity = { authenticated: true };
  }
  return validity;
}

export function createSessionValidator(fetchFn: typeof fetch): () => Promise<SessionValidity> {
  return async () => {
    return fetchSessionStatus(fetchFn);
  };
}

async function ignoreAbortedLockRequest(request: Promise<unknown>): Promise<void> {
  // become a no-op, never propagate out of this fire-and-forget lock request.
  try {
    await request;
  } catch {
    // The pending lock request was aborted before acquisition — not an error.
  }
}

export function createLeaderElector(
  lockName: string,
  lockRequest: LockRequestFn | null,
  handlers: LeaderElectorHandlers
): LeaderElector {
  let leader = false;
  let active = false;
  let releaseHold: (() => void) | null = null;
  let abortController: AbortController | null = null;

  function start(): void {
    if (!active) {
      active = true;
      if (lockRequest) {
        abortController = new AbortController();
        // navigator.locks forces a callback boundary; it is async and awaits the
        // manual-resolve release hold — the only way to keep the lock held until stop().
        const request = lockRequest(lockName, { signal: abortController.signal }, async () => {
          if (active) {
            leader = true;
            handlers.onBecomeLeader();
            await new Promise<void>((resolve) => {
              releaseHold = resolve;
            });
          }
        });
        void ignoreAbortedLockRequest(request);
      } else {
        leader = true;
        handlers.onBecomeLeader();
      }
    }
  }

  function stop(): void {
    if (active) {
      active = false;
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
      if (releaseHold) {
        releaseHold();
        releaseHold = null;
      }
      if (leader) {
        leader = false;
        handlers.onResignLeader();
      }
    }
  }

  function isLeader(): boolean {
    return leader;
  }

  return { start: start, stop: stop, isLeader: isLeader };
}

export function runSessionStatus(deps: SessionStatusDeps): () => void {
  let stopped = false;
  let checking = false;
  let intervalHandle: number | null = null;
  // Seed with the org the page rendered for, so the first poll of the same org is not
  // treated as a change (which would broadcast org-switch and reload legacy listeners).
  let lastObservedOrg = '';
  if (typeof deps.initialObservedOrg === 'string') {
    lastObservedOrg = deps.initialObservedOrg;
  }

  async function check(): Promise<void> {
    if (!stopped && !checking) {
      checking = true;
      // tick" (a value), never throw out of this fire-and-forget session-status poll.
      let validity: SessionValidity | null = null;
      try {
        validity = await deps.validate();
      } catch {
        validity = null;
      }
      checking = false;
      if (!stopped && validity) {
        if (!validity.authenticated) {
          if (validity.organizationDeactivated === true && deps.onOrgDeactivated) {
            deps.onOrgDeactivated();
          } else {
            deps.onUnauthenticated();
          }
        } else {
          const selection = OrganizationSelections.From(validity);
          const observedOrg = OrganizationSelections.Key(selection);
          if (selection && observedOrg !== lastObservedOrg) {
            const switched = lastObservedOrg.length > 0;
            lastObservedOrg = observedOrg;
            if (deps.onOrgChange) {
              deps.onOrgChange(selection, switched);
            }
          }
        }
      }
    }
  }

  const elector = createLeaderElector(deps.lockName, deps.lockRequest, {
    onBecomeLeader: () => {
      // Interval only — no immediate poll. Callers enable this after boot APIs
      // already proved the session (e.g. refresh of a logged-in page). Immediate
      // validate-session would re-pay Cognito+DB cost the user just paid.
      intervalHandle = deps.setInterval(() => {
        void check();
      }, deps.intervalMs);
    },
    onResignLeader: () => {
      if (intervalHandle !== null) {
        deps.clearInterval(intervalHandle);
        intervalHandle = null;
      }
    },
  });
  elector.start();
  const stopWatchingVisibility = watchTabVisible(() => {
    void check();
  });

  return () => {
    stopped = true;
    stopWatchingVisibility();
    elector.stop();
  };
}

function watchTabVisible(onVisible: () => void): () => void {
  let unsubscribe = () => undefined as void;
  if (typeof document !== 'undefined' && typeof window !== 'undefined') {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onVisible();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', onVisible);
    unsubscribe = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', onVisible);
    };
  }
  return unsubscribe;
}
